import type { FileUploadDefinition } from "../types";
import { formatBytes, getUploadFileLimitBytes, uploadLimitDescription } from "./upload-utils";

export type UploadFailure = { title: string; description: string };

export class PartnerUploadError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "PartnerUploadError";
  }
}

const CONTACT_MANAGER = "계속 실패하면 크리에이트립 담당자에게 문의해주세요.";
const DRAFT_CONFLICT_MESSAGES = new Set(["Draft version conflict", "Draft is no longer editable", "File not found or draft is no longer editable"]);
const FORMAT_LABELS: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
  "video/mp4": "MP4",
  "video/quicktime": "MOV",
  "video/webm": "WEBM",
  "application/pdf": "PDF",
};

function acceptedTypes(accept?: string): string[] {
  return (accept ?? "").split(",").map((type) => type.trim()).filter(Boolean);
}

function acceptedFormatLabel(accept?: string): string {
  return [...new Set(acceptedTypes(accept).map((type) => FORMAT_LABELS[type]).filter(Boolean))].join(", ");
}

function isHeic(file: Pick<File, "name" | "type">): boolean {
  return /^image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

function emptyFileFailure(prefix: string): UploadFailure {
  return { title: `${prefix}빈 파일이에요`, description: "파일이 정상적으로 열리는지 확인한 뒤 다시 올려주세요." };
}

function oversizedFailure(title: string, accept?: string): UploadFailure {
  return { title, description: `${uploadLimitDescription(accept)}로 줄여서 다시 올려주세요.` };
}

function unsupportedFormatFailure(file: Pick<File, "name" | "type">, accept?: string): UploadFailure {
  if (isHeic(file)) {
    return {
      title: `${file.name}: HEIC/HEIF 사진 형식은 첨부할 수 없어요`,
      description: "JPG 또는 PNG로 변환해서 다시 올려주세요. 아이폰이라면 설정 > 카메라 > 포맷에서 '높은 호환성'을 선택하면 JPG로 저장돼요.",
    };
  }
  const formats = acceptedFormatLabel(accept);
  return {
    title: `${file.name}: 지원하지 않는 파일 형식이에요`,
    description: formats ? `${formats} 형식으로 변환해서 다시 올려주세요.` : "다른 형식으로 변환해서 다시 올려주세요.",
  };
}

export function validateUploadSelection(definition: FileUploadDefinition, existingCount: number, files: File[]): UploadFailure | null {
  if (definition.maxFiles && existingCount + files.length > definition.maxFiles) {
    const remaining = Math.max(definition.maxFiles - existingCount, 0);
    return {
      title: `최대 ${definition.maxFiles}개까지 첨부할 수 있어요`,
      description: remaining > 0
        ? `지금 ${files.length}개를 선택했어요. ${remaining}개 이하로 골라서 다시 올려주세요.`
        : "이미 최대 개수를 첨부했어요. 기존 파일을 삭제한 뒤 다시 올려주세요.",
    };
  }
  const allowedTypes = acceptedTypes(definition.accept);
  const unsupportedFile = allowedTypes.length > 0 ? files.find((file) => !allowedTypes.includes(file.type)) : undefined;
  if (unsupportedFile) return unsupportedFormatFailure(unsupportedFile, definition.accept);
  const emptyFile = files.find((file) => file.size === 0);
  if (emptyFile) return emptyFileFailure(`${emptyFile.name}: `);
  const oversizedFile = files.find((file) => file.size > getUploadFileLimitBytes(file));
  if (oversizedFile) return oversizedFailure(`${oversizedFile.name}: 파일 용량이 너무 커요 (${formatBytes(oversizedFile.size)})`, definition.accept);
  return null;
}

export function describeUploadFailure(error: unknown, file: Pick<File, "name" | "type"> | undefined, accept?: string): UploadFailure {
  const prefix = file ? `${file.name}: ` : "";
  if (error instanceof PartnerUploadError) {
    if (error.status === 410) return { title: "초대 링크가 만료되어 첨부할 수 없어요", description: "크리에이트립 담당자에게 새 상품요청서 링크를 요청해주세요." };
    if (error.status === 404) return { title: "초대 링크를 찾을 수 없어요", description: "링크 주소를 다시 확인하거나 크리에이트립 담당자에게 새 링크를 요청해주세요." };
    if (error.status === 409 && error.message === "Invite already submitted") return { title: "이미 제출된 요청서라 파일을 추가할 수 없어요", description: "수정이 필요하면 크리에이트립 담당자에게 문의해주세요." };
    if (error.status === 409 && DRAFT_CONFLICT_MESSAGES.has(error.message)) return { title: "다른 곳에서 요청서가 함께 수정되고 있어요", description: "페이지를 새로고침해 최신 내용을 불러온 뒤 다시 첨부해주세요." };
    if (error.status === 413) return oversizedFailure(`${prefix}파일 용량이 너무 커요`, accept);
    if (error.status === 415 && file) return unsupportedFormatFailure(file, accept);
    if (error.status === 429) return { title: "짧은 시간에 첨부 요청이 너무 많았어요", description: `잠시 후 다시 시도해주세요. ${CONTACT_MANAGER}` };
    if (error.status === 400 && error.message === "File is empty") return emptyFileFailure(prefix);
    if (error.status >= 500) return { title: `${prefix}일시적인 서버 오류로 첨부하지 못했어요`, description: `잠시 후 다시 시도해주세요. ${CONTACT_MANAGER}` };
  }
  // fetch 자체가 실패하면 브라우저는 TypeError를 던진다.
  if (error instanceof TypeError) return { title: `${prefix}네트워크 연결이 불안정해 첨부하지 못했어요`, description: `인터넷 연결을 확인한 뒤 다시 시도해주세요. 파일이 크면 Wi-Fi 환경에서 올려주세요.` };
  const message = error instanceof Error && /[가-힣]/.test(error.message) ? error.message : null;
  return { title: `${prefix}첨부하지 못했어요`, description: message ? `${message} ${CONTACT_MANAGER}` : `잠시 후 다시 시도해주세요. ${CONTACT_MANAGER}` };
}
